import { FormDialog } from "@dzambalaorg/mui-dialog-form";
import { SelectInput, TextInput } from "@dzambalaorg/mui-inputs-core";
import { RichTextEditor } from "@dzambalaorg/mui-inputs-draft";
import { AvatarInput } from "@dzambalaorg/mui-inputs-image-firebase";
import { EntityInput } from "@dzambalaorg/mui-inputs-entity";
import { TemplateAssetInput } from "@dzambalaorg/mui-inputs-asset";
import { ContractStatus, ILootBox, LootBoxStatus, ModuleType, TokenType } from "@framework/types";
import { IMutationProps, useDialogForm } from "@dzambalaorg/react-hooks";
import { cleanUpAsset } from "@framework/exchange";

import { validationSchema } from "./validation";
import { MaxInput } from "./max-input";
import { MinInput } from "./min-input";

export interface ILootBoxDialogFormProps extends IMutationProps, Pick<ReturnType<typeof useDialogForm>, "mode"> {
  open: boolean;
  selectedItem: ILootBox | null;
}

export const LootBoxDialogForm = (props: ILootBoxDialogFormProps) => {
  const { selectedItem, open, mode, ...rest } = props;

  if (!open || !selectedItem) {
    return null;
  }

  const { id, title, description, content, imageUrl, lootBoxStatus, template, min, max } = selectedItem;

  const fixedValues = {
    id,
    title,
    description,
    content,
    imageUrl,
    lootBoxStatus,
    template,
    min: min || 1,
    max: max || content?.components.length || 2,
  };

  return (
    <FormDialog
      open={open}
      initialValues={fixedValues}
      validationSchema={validationSchema}
      message={`dialogs.${mode}`}
      testId="LootBoxEditForm"
      filter={({ template, title, description, imageUrl, content, lootBoxStatus, min, max }) =>
        mode === "edit"
          ? {
              title,
              description,
              imageUrl,
              content: cleanUpAsset(content) as any,
              price: cleanUpAsset(template?.price) as any,
              lootBoxStatus,
              min,
              max,
            }
          : {
              contractId: template?.contractId,
              title,
              description,
              imageUrl,
              content: cleanUpAsset(content) as any,
              price: cleanUpAsset(template?.price) as any,
              min,
              max,
            }
      }
      {...rest}
    >
      <EntityInput
        name="template.contractId"
        controller="contracts"
        fetchData={{
          contractModule: [ModuleType.LOOT],
          contractStatus: [ContractStatus.ACTIVE, ContractStatus.NEW],
        }}
        readOnly={mode === "edit"}
      />
      <TextInput name="title" required />
      <RichTextEditor
        name="description"
        slotProps={{
          inputLabel: {
            required: true,
          },
        }}
      />
      <MinInput />
      <MaxInput />
      <TemplateAssetInput
        required
        autoSelect
        multiple
        prefix="content"
        contract={{
          data: {
            contractModule: [ModuleType.HIERARCHY],
            contractStatus: [ContractStatus.ACTIVE, ContractStatus.NEW],
          },
        }}
        forceAmount
        readOnly={!!id}
      />
      <TemplateAssetInput
        required
        autoSelect
        multiple
        prefix="template.price"
        tokenType={{ disabledOptions: [TokenType.ERC721, TokenType.ERC998] }}
        contract={{
          data: {
            contractModule: [ModuleType.HIERARCHY],
            contractStatus: [ContractStatus.ACTIVE, ContractStatus.NEW],
          },
        }}
      />
      {mode === "edit" && <SelectInput name="lootBoxStatus" options={LootBoxStatus} />}
      <AvatarInput name="imageUrl" required />
    </FormDialog>
  );
};
